/**
 * SED refresh planner — decide the SMALLEST set of sections worth refetching.
 *
 * WHY THIS EXISTS. Two mechanisms already exist and never meet:
 *
 *   - `SED_SECTION_TTL_DAYS` encodes how fast each source actually changes —
 *     publicSentiment 1 day, interconnection 7, externalLiterature 14,
 *     regulatory 30, adamsDocuments 90, siteProfile 365. That table IS the
 *     cost model: it says what is worth paying to refetch and how often.
 *   - `topUpSiteSed({ onlyProviders })` can already refetch a subset.
 *
 * Nothing connects them. Measured on Redhead (2026-07-27) all 28 provider
 * sections carried the SAME timestamp — the SED had only ever moved as one
 * block, so keeping it current meant a full rebuild, and a full rebuild is
 * expensive enough that in practice nobody ran one. The result was a four-day-
 * old SED with a one-day TTL on its most volatile section.
 *
 * The other half is that a section can be stale for a reason a clock cannot
 * see. Intent-stamped sections (technology / cooling / power / firmCapacity /
 * lcoe) carry 365-day TTLs precisely because they track the USER's intent
 * rather than an upstream feed — their own comment says they "refresh on
 * intent edit, not a provider clock" — but nothing ever refreshed them on an
 * intent edit. Editing intent left them describing the previous project.
 *
 * So a refresh is warranted when EITHER:
 *   1. the section's TTL has expired (the source moved), or
 *   2. an input it derives from changed after it was collected (we moved).
 *
 * Pure and dependency-light so it can be unit-tested and reused by the UI, a
 * scheduled job, or a pre-report hook without dragging in Firestore.
 */
import { SED_SECTION_TTL_DAYS } from '../sedStaleness';
import type { SedSectionId, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent } from '../../types/siteRag';
import { computeAllSectionHashes } from './sedFingerprint';
import { SED_DEPENDENCY_GRAPH } from './sedDependencyGraph';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { hashProjectIntent } from './shouldBootstrap';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';

const MS_PER_DAY = 86_400_000;

/** Why a section is being refreshed — carried through so the UI can explain. */
export type RefreshReason =
  | 'ttl-expired'      // the upstream source has had time to move
  | 'intent-changed'   // the user changed what the section is derived from
  | 'documents-changed' // a document landed that this section can read
  | 'never-collected';  // no timestamp at all

export interface SectionRefreshNeed {
  sectionId: SedSectionId;
  reason: RefreshReason;
  /** Age in whole days, or null when never collected. */
  ageDays: number | null;
  /** The TTL that applies, for explaining the decision to a user. */
  ttlDays: number;
}

/**
 * Sections derived from Project Intent rather than an upstream feed.
 *
 * Their long TTLs are correct — no clock should refetch them — but an intent
 * edit invalidates them immediately. Kept as an explicit list so adding one is
 * a reviewable one-line change rather than a guess from the TTL value.
 */
export const INTENT_DERIVED_SECTIONS: ReadonlySet<string> = new Set([
  'technology', 'cooling', 'power', 'firmCapacity', 'lcoe',
]);

/**
 * Sections a newly-uploaded document can populate.
 *
 * Deliberately NOT "every section": a document changes what we know about the
 * subjects it covers, and refetching an unrelated upstream feed because a PDF
 * landed is spend with no expected information gain.
 *
 * D10 — widened from 7 to the honest superset: every section with a
 * channel-A extraction schema (SECTION_SCHEMAS in extractFromDocument.ts)
 * plus every channel-B primary section (REQUIREMENT_TO_SECTION_MAP[req][0]
 * for extractable requirements). The old list undercounted by half, so a
 * flood cert or utility letter upload never marked its own section stale.
 * A sync test (sedRefreshPlan.test.ts) pins this against both sources —
 * add a schema, the test tells you to add the section here.
 */
export const DOCUMENT_FED_SECTIONS: ReadonlySet<string> = new Set([
  // Channel-A section schemas.
  'climate', 'grid', 'flood', 'environmental', 'waterStress', 'regulatory',
  'seismic', 'electricity', 'airQuality', 'nuclearInfra', 'solar', 'wind',
  'connectivity', 'parcel',
  // P6 — municipal water / wastewater sub-objects from utility letters.
  'waterResource',
  // Channel-B primary sections not already above.
  'geotechnical', 'survey', 'mineralResources', 'culturalResources',
  'publicSentiment',
  // Legacy members that predate the widening (doc-fed via other routes).
  'siteResearch', 'onSiteInfrastructure',
]);

export interface RefreshPlanInputs {
  /** Per-section collection timestamps, section id → ISO string. */
  collectedAt: Readonly<Record<string, string | undefined>>;
  /**
   * Per-section "last refresh ATTEMPT" stamps, section id → ISO string.
   *
   * The nag-loop fix: `collectedAt` records when a section's VALUE last
   * changed, but the plan's question is "have you LOOKED since the input
   * changed?". Without this, a refresh that ran and found nothing new could
   * never clear the banner — the user refreshes, nothing changes, the
   * banner demands the same refresh again, forever. A check counts as
   * looking: staleness compares against max(collected, checked).
   */
  checkedAt?: Readonly<Record<string, string | undefined>>;
  /** When Project Intent was last edited, if ever. */
  intentUpdatedAt?: string | null;
  /** Actual changed intent dependencies, when the SED retained its full input.
   * This supersedes the coarse configuration timestamp, which also advances
   * for display mode and unrelated preferences. Absence keeps legacy behavior. */
  intentChangedSections?: ReadonlySet<string>;
  /** Selected review scope; does not change the canonical evidence or gates. */
  inScope?: ReadonlySet<string>;
  /** When a document was last added or re-extracted, if ever. */
  documentsUpdatedAt?: string | null;
  /** Evaluation time — injected so tests are deterministic. */
  now?: number;
  /**
   * Sections the caller does not want touched (already refreshing, waived,
   * or explicitly pinned by the user).
   */
  exclude?: ReadonlySet<string>;
  /**
   * Sections the refresh button can actually act on. When provided, the
   * plan only ever asks for these — a section with no provider (parcel,
   * siteProfile, …) in the plan is a demand the button cannot satisfy,
   * which made "N never collected" a permanent banner fixture.
   */
  refreshable?: ReadonlySet<string>;
}

function ageDaysOf(iso: string | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((now - t) / MS_PER_DAY);
}

/**
 * Which sections should be refetched, and why.
 *
 * Returns ONLY sections that need work. An empty array means the SED is
 * current and nothing should be spent — that is the common case and the whole
 * point of planning rather than rebuilding.
 */
export function planSedRefresh(inputs: RefreshPlanInputs): SectionRefreshNeed[] {
  const now = inputs.now ?? Date.now();
  const exclude = inputs.exclude ?? new Set<string>();
  const intentAt = inputs.intentUpdatedAt ? Date.parse(inputs.intentUpdatedAt) : NaN;
  const docsAt = inputs.documentsUpdatedAt ? Date.parse(inputs.documentsUpdatedAt) : NaN;

  const out: SectionRefreshNeed[] = [];
  for (const [sectionId, ttlDays] of Object.entries(SED_SECTION_TTL_DAYS)) {
    if (exclude.has(sectionId)) continue;
    if (inputs.inScope && !inputs.inScope.has(sectionId)) continue;
    // A plan entry the refresh button cannot act on is a permanent nag.
    if (inputs.refreshable && !inputs.refreshable.has(sectionId)) continue;
    const iso = inputs.collectedAt[sectionId];
    const collectedMs = iso ? Date.parse(iso) : NaN;
    const checkedIso = inputs.checkedAt?.[sectionId];
    const checkedMs = checkedIso ? Date.parse(checkedIso) : NaN;
    // "Looked at" = the later of value-changed and refresh-attempted. Every
    // staleness comparison below runs against this, so a refresh that found
    // nothing new still clears the banner instead of re-demanding itself.
    const effectiveMs = Number.isFinite(collectedMs)
      ? (Number.isFinite(checkedMs) ? Math.max(collectedMs, checkedMs) : collectedMs)
      : checkedMs;
    const effectiveIso = Number.isFinite(effectiveMs) ? new Date(effectiveMs).toISOString() : undefined;
    const ageDays = ageDaysOf(effectiveIso, now);

    if (ageDaysOf(iso, now) === null) {
      // An intent producer has no new answer to compute when its exact frozen
      // dependencies are unchanged. Unavailable stays unavailable; this only
      // avoids asking for the same undecided input again.
      if (INTENT_DERIVED_SECTIONS.has(sectionId)
        && inputs.intentChangedSections && !inputs.intentChangedSections.has(sectionId)) continue;
      // Never collected — but a recent CHECK means someone already asked and
      // the provider had nothing; nagging again within the TTL just loops.
      // The check ages like any other timestamp, so the plan re-asks once it
      // expires.
      if (ageDays !== null && ageDays < ttlDays) continue;
      out.push({ sectionId: sectionId as SedSectionId, reason: 'never-collected', ageDays: null, ttlDays });
      continue;
    }

    // An input changing beats the clock. A section collected this morning is
    // still WRONG if the intent it derives from changed this afternoon, and no
    // TTL will ever notice that.
    if (INTENT_DERIVED_SECTIONS.has(sectionId)
      && (!inputs.intentChangedSections || inputs.intentChangedSections.has(sectionId))
      && Number.isFinite(intentAt) && Number.isFinite(effectiveMs)
      && intentAt > effectiveMs) {
      out.push({ sectionId: sectionId as SedSectionId, reason: 'intent-changed', ageDays, ttlDays });
      continue;
    }

    if (DOCUMENT_FED_SECTIONS.has(sectionId)
      && Number.isFinite(docsAt) && Number.isFinite(effectiveMs)
      && docsAt > effectiveMs) {
      out.push({ sectionId: sectionId as SedSectionId, reason: 'documents-changed', ageDays, ttlDays });
      continue;
    }

    if (ageDays !== null && ageDays >= ttlDays) {
      out.push({ sectionId: sectionId as SedSectionId, reason: 'ttl-expired', ageDays, ttlDays });
    }
  }

  // Most-overdue first, so a caller that can only afford part of the plan
  // spends on the sections that have drifted furthest.
  return out.sort((a, b) => {
    const aOver = a.ageDays === null ? Number.POSITIVE_INFINITY : a.ageDays - a.ttlDays;
    const bOver = b.ageDays === null ? Number.POSITIVE_INFINITY : b.ageDays - b.ttlDays;
    return bOver - aOver;
  });
}

/** One-line summary for a UI that must justify asking to spend. */
export function describeRefreshPlan(plan: readonly SectionRefreshNeed[]): string {
  if (plan.length === 0) return 'Evidence is current — nothing to refresh.';
  const byReason = plan.reduce<Record<string, number>>((acc, n) => {
    acc[n.reason] = (acc[n.reason] ?? 0) + 1;
    return acc;
  }, {});
  const parts: string[] = [];
  if (byReason['intent-changed']) parts.push(`${byReason['intent-changed']} affected by an intent change`);
  if (byReason['documents-changed']) parts.push(`${byReason['documents-changed']} affected by a new document`);
  if (byReason['ttl-expired']) parts.push(`${byReason['ttl-expired']} past their refresh window`);
  if (byReason['never-collected']) parts.push(`${byReason['never-collected']} never collected`);
  return `${plan.length} section${plan.length === 1 ? '' : 's'} to refresh — ${parts.join(', ')}.`;
}

/** Hash only the declared input values/unknown markers a section consumes.
 * Separate from the canonical source fingerprint: no source is stamped fresh. */
export function sedSectionIntentInputHash(section: SedSectionId, intent: ProjectIntent | null | undefined): string {
  const fields = SED_DEPENDENCY_GRAPH[section].intentFields.map(field => {
    let value: unknown = intent;
    for (const part of field.split('.')) {
      value = value != null && typeof value === 'object'
        ? (value as Record<string, unknown>)[part] : undefined;
    }
    return [field, value, isIntentFieldExplicitlyUnknown(intent, field)];
  });
  return hashProjectIntent({ fields });
}

/** Compare only declared intent dependencies with the full input actually
 * retained in this SED. Both hashes use identical source/coordinate inputs;
 * this is a read-side work plan, never a freshness stamp or rebuilt evidence. */
export function changedSedIntentSections(
  sed: SiteEvidenceDocument,
  intent: ProjectIntent | null | undefined,
): ReadonlySet<string> | undefined {
  if (!sed.fullProjectIntent && !sed.sectionIntentInputHashes) return undefined;
  const shared = { coords: sed.location };
  const frozen = computeAllSectionHashes({ ...shared, intent: sed.fullProjectIntent }, sed);
  const current = computeAllSectionHashes({ ...shared, intent }, sed);
  return new Set([...current].filter(([id, hash]) => {
    const consumed = sed.sectionIntentInputHashes?.[id];
    if (consumed) return consumed !== sedSectionIntentInputHash(id, intent);
    // A partial legacy stamp does not make any other section current.
    if (!sed.fullProjectIntent) return true;
    return hash !== frozen.get(id) || SED_DEPENDENCY_GRAPH[id].intentFields.some(field =>
      isIntentFieldExplicitlyUnknown(sed.fullProjectIntent, field)
        !== isIntentFieldExplicitlyUnknown(intent, field),
    );
  }).map(([id]) => id));
}

/** Stage 1 currency follows its real screening analysis requirements. Stage 2
 * retains the complete source worklist; earlier screening facts still matter. */
export function sedRefreshScopeForStage(stage: 1 | 2 | undefined): ReadonlySet<string> | undefined {
  if (stage !== 1) return undefined;
  return new Set(Object.values(REPORT_TYPE_REGISTRY)
    .filter(type => type.stage === 1 && (type.status === 'active' || type.status === 'beta'))
    .flatMap(type => type.requiredSedSections));
}
